import classnames from "classnames";
import { useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useIsOrCanBeCommunityMember } from "@circle-react/contexts";
import { isPostDetailsPage } from "@circle-react/helpers/isPostDetailsPage";
import { Icon } from "@circle-react-shared/Icon";
import { useNavigateToPost } from "../PostModal/hooks/useNavigateToPost";
import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";
import { CommentCountLink } from "./CommentCountLink";
import { PostLikedByMemberStackV3 } from "./PostLikedByMemberStackV3";

interface CommentsFormProps {
  shouldForceSamePageInteractions?: boolean;
  shouldOpenCommentPage: boolean;
  showCommentAndFocusCommentForm: () => void;
  toggleComment: () => void;
}

export const CommentsForm = ({
  shouldOpenCommentPage,
  showCommentAndFocusCommentForm,
  toggleComment,
  shouldForceSamePageInteractions = false,
}: CommentsFormProps) => {
  const isOrCanBeCommunityMember = useIsOrCanBeCommunityMember();
  const location = useLocation();
  const { navigateToPost } = useNavigateToPost();
  const { params, hasNextPage } = usePostNavigationContext();

  const { post } = useCurrentPostHelpers();
  const {
    is_comments_closed: isCommentsClosed,
    is_comments_enabled: isCommentsEnabled,
    comments_count: commentsCount = 0,
    post_details_loaded,
  } = post;

  const shouldShowPostModal = !isPostDetailsPage();

  const isCommentsForm = isCommentsEnabled && !shouldOpenCommentPage;

  if (!isCommentsForm) {
    return (
      <div className="engagement__comments ml-auto mr-0 flex items-center gap-2">
        <PostLikedByMemberStackV3 shouldDisplayVerticalDivider={false} />
      </div>
    );
  }

  return (
    <>
      {!isCommentsClosed && isOrCanBeCommunityMember && (
        <button
          type="button"
          onClick={() => {
            shouldShowPostModal
              ? navigateToPost({
                  newPost: post,
                  prevLocation: location,
                  params,
                  hasNextPage,
                })
              : showCommentAndFocusCommentForm();
          }}
          aria-label={t("cap_comment_aria_label", {
            title: post?.name ?? t("this_post"),
          })}
          className={classnames(
            "action-comment text-default hover:!text-dark focus-visible:outline-secondary flex items-center px-1 py-0.5 text-sm font-medium transition-colors duration-150 ease-in-out focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          )}
        >
          <span className="action__icon">
            <Icon type="24-comment" size={24} />
          </span>
        </button>
      )}
      <div
        className="engagement__comments ml-auto mr-0 flex items-center gap-2"
        data-testid="engagement-comments"
      >
        <PostLikedByMemberStackV3 />
        <CommentCountLink
          count={commentsCount}
          toggleComment={toggleComment}
          hasPostDetailsLoaded={post_details_loaded}
          shouldForceSamePageInteractions={shouldForceSamePageInteractions}
        />
      </div>
    </>
  );
};
